import { useEffect, useMemo, useState } from 'react';

import { AutocompleteInputChangeReason, Typography } from '@mui/material';
import { Stack, SxProps } from '@mui/system';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import OrganisationsService from '~features/organisations/services/organisationsService';
import { BaseReference } from '~interfaces';
import Organisation from '~interfaces/organisation';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';

import AsyncAutocomplete from './asyncAutocomplete';

const service = getService(OrganisationsService);

interface OrganisationsAutocompleteProps {
	onValueChanged: (newValue?: Organisation) => void;
	value?: string | Organisation;
	placeholder?: string;
	disableClearable?: boolean;
	required?: boolean;
	label?: string;
	sx?: SxProps;
}

const OrganisationsAutocomplete = ({
	value,
	placeholder,
	disableClearable = false,
	label,
	required = false,
	...props
}: OrganisationsAutocompleteProps) => {
	const { t } = useTranslation();

	// Data loading
	// Not really the best way, but I'm assuming we wont have over 500 clients for a while.
	// Mostly because of time constraint and now wanting to figure out the paging in the auto complete
	// I took a little shortcut. Will revisit this at a later moment.
	const { data } = useSWR(
		['organisations'],
		() => service.getOrganisations({ page: '1', pageSize: '500' }),
		{
			keepPreviousData: false,
			revalidateFirstPage: true,
		}
	);

	// Hooks and states
	const [filteredOrganisations, setFilteredOrganisations] = useState([] as Organisation[]);
	const organisations = useMemo(() => data?.results ?? [], [data?.results]);

	useEffect(() => {
		// This is an okay use-case when data is fetched from API and depends on user input.
		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setFilteredOrganisations(organisations);
	}, [organisations]);

	const selectedOrganisation = useMemo(() => {
		const selectedId = (value as Organisation)?.id ?? value;
		if (selectedId == null || selectedId === '') {
			return null;
		}

		return organisations.find((organisation) => organisation.id === selectedId) ?? null;
	}, [organisations, value]);

	// Input handling
	const handleInputChange = (
		_event: React.SyntheticEvent,
		value: string,
		_reason: AutocompleteInputChangeReason
	) => {
		if (value.length <= 0) {
			setFilteredOrganisations(organisations);
		} else {
			setFilteredOrganisations(
				organisations.filter((organisation) =>
					organisation.name.toLowerCase().includes(value.toLowerCase())
				)
			);
		}
	};

	const handleOptionSelected = (value: BaseReference | undefined) => {
		props.onValueChanged(organisations.find((organisation) => organisation.id == value?.id));
	};

	return (
		<Stack sx={{ gap: Dimens.spacingXs }}>
			{label && <Typography variant='labelSm'>{label}</Typography>}
			<AsyncAutocomplete
				value={selectedOrganisation}
				placeholder={placeholder ?? t('organisation.labels.single', { ns: 'entities' })}
				noOptionsText={t('noResults', { ns: 'system' })}
				options={filteredOrganisations}
				onInputChange={handleInputChange}
				onOptionSelected={handleOptionSelected}
				debounce={false}
				required={required}
				enableInfiniteScroll={false}
				disableClearable={disableClearable}
				sx={{
					width: 200,
					...props.sx,
				}}
			/>
		</Stack>
	);
};

export default OrganisationsAutocomplete;
